import { useQuery } from "@tanstack/react-query";
import Services from "./services";
import { parseConfigArray, parseJsonConfig } from "../../utils/util";
import { parseTestPrefillConfigRaw } from "./testPrefillConfig";

export const useGetCarMakers = () => {
  return useQuery(
    ["GET_CAR_MAKERS"],
    async () => {
      const response = await Services.getCarMakers();
      return response;
    },
    { staleTime: Infinity },
  );
};

export const useGetStateCity = () => {
  return useQuery(
    ["GET_STATE_CITY"],
    async () => {
      const response = await Services.getStateCity();
      return response;
    },
    { staleTime: Infinity },
  );
};

export const useGetInsurers = () => {
  return useQuery(
    ["GET_INSURERS"],
    async () => {
      const response = await Services.getInsurers();
      return response;
    },
    { staleTime: Infinity },
  );
};

export const useGetOrderMediaConfig = () => {
  return useQuery(
    ["GET_MOBILE_CONFIG"],
    async () => {
      const response = await Services.getOrderMediaConfig();
      return {
        order_media_type: response.order_media_type,
        order_cancellation_reasons: parseConfigArray(
          response.order_cancellation_reasons,
        ),
        min_reason_length: response.min_reason_character_length,
        quick_etas_mins: response.quick_etas_mins,
        threshold_giving_eta_to_customer_mins:
          response.threshold_giving_eta_to_customer_mins,
        threshold_ack_order_mins: response.threshold_ack_order_mins,
        quick_etas_cbo_order_days: response.quick_etas_cbo_order_days,
        active_hours_start: response.active_hours_start,
        active_hours_end: response.active_hours_end,
        allOrders_query_staleTime: response.orca_allorders_query_stale_time_min,
        allOrders_query_refetchInterval:
          response.orca_allorders_query_refetch_interval_min,
        global_query_stale_time: response.orca_query_stale_time_min,
        global_query_refetch_interval: response.orca_query_refetch_interval_min,
        mobile_numbers_allowed_to_proceed_ready_for_dispatch_in_orca:
          response?.mobile_numbers_allowed_to_proceed_ready_for_dispatch_in_orca ||
          "",
        typesense_api_key: response?.typesense_api_key,
        typesense_host: response?.typesense_host,
        typesense_api_key_supplierinventories:
          response?.typesense_api_key_supplierinventories,
        typesense_host_supplierinventories:
          response?.typesense_host_supplierinventories,
        default_supplier_discovery_distance_range_filter:
          response?.default_supplier_discovery_distance_range_filter,
        supplier_discovery_suppliers_limit:
          response?.supplier_discovery_suppliers_limit,
        supplier_discovery_parts_limit:
          response?.supplier_discovery_parts_limit,
        garaaz_event_config: response?.garaaz_event_config,
        suprsend_orca_public_api_key: response?.suprsend_orca_public_api_key,
        orca_shipment_refetch_cooldown_seconds:
          response?.orca_shipment_refetch_cooldown_seconds,
        ff_orca_oem_catalogue: response?.ff_orca_oem_catalogue,
        license_plate_number_regex: response?.license_plate_number_regex,
        chat_notification_sound_orca_supplier:
          response?.chat_notification_sound_orca_supplier,
        chat_notification_sound_orca_customer:
          response?.chat_notification_sound_orca_customer,
        notification_intent_sounds_mapping:
          response?.orca_notification_intent_sounds_mapping,
        is_customer_chat_allowed_on_orca:
          response?.is_customer_chat_allowed_on_orca,
        allowed_number_of_buckets_in_supplier_discovery:
          response?.allowed_number_of_buckets_in_supplier_discovery,
        max_broadcast_suppliers_limit: response?.max_broadcast_suppliers_limit,
        ff_use_lmd_package: response?.ff_use_lmd_package,
        ff_freeze_order_items_post_part_conversion:
          response?.ff_freeze_order_items_post_part_conversion,
        ff_share_enquiry_estimate_to_customer:
          response?.ff_share_enquiry_estimate_to_customer,
        ff_offline_discovery_broadcast_enabled_orca:
          response?.ff_offline_discovery_broadcast_enabled_orca,
        ff_allow_consolidated_invoice_generation:
          response?.ff_allow_consolidated_invoice_generation,
        ff_disable_manual_delivery_supplier_selection:
          response?.ff_disable_manual_delivery_supplier_selection,
        ff_show_garaaz_slot_on_standard_delivery:
          response?.ff_show_garaaz_slot_on_standard_delivery,
        ff_one_shipment_multiple_customer_order:
          response?.ff_one_shipment_multiple_customer_order,
        part_confirmation_eligible_supplier_ids: parseConfigArray(
          response?.part_confirmation_eligible_supplier_ids,
          true,
        ),
        ff_part_confirmation_bucket:
          response?.ff_part_confirmation_bucket,
        enforce_digital_estimate_associate_supplier_ids: parseConfigArray(
          response?.enforce_digital_estimate_associate_supplier_ids,
          true,
        ),
        max_split_allowed_share_to_customer:
          response?.max_split_allowed_share_to_customer ?? 1,
        hldLocaleDistanceMetres: response?.hldLocaleDistanceMetres,
        logistics_min_delivery_fee_rs:
          response?.logistics_min_delivery_fee_rs ?? 30,
        logistics_delivery_fee_per_km_rs:
          response?.logistics_delivery_fee_per_km_rs ?? 13,
        logistics_cpt_hours_offset: response?.logistics_cpt_hours_offset,
        instant_delivery_min_delivery_cost_rs:
          response?.instant_delivery_min_delivery_cost_rs ?? 50,
        zoho_creator_part_master_application:
          response?.zoho_creator_part_master_application,
        zoho_creator_customer_payment_links_application:
          response?.zoho_creator_customer_payment_links_application,
        garaaz_supplier_id: response?.garaaz_supplier_id,
        eligible_insurer_families_for_approval: parseConfigArray(
          response?.eligible_insurer_families_for_approval,
        ),
        default_broadcast_recipient: parseJsonConfig(
          response?.default_broadcast_recipient,
          [] as Array<{ phone?: string; email?: string; name?: string }>,
        ),
        orca_test_order_prefill_config: parseTestPrefillConfigRaw(
          response?.orca_test_order_prefill_config,
        ),
        devs_user_ids: parseConfigArray(response?.devs_user_ids, true),
        mgp_distributor:
          response?.mgp_distributor || response?.iam_parent_supplier_id,
        override_edit_user_ids:
          response?.override_edit_user_ids ||
          response?.allow_edit_tt_user_ids ||
          "",
        whitelisted_field_for_new_delivery_option:
          response?.whitelisted_field_for_new_delivery_option || "",
        whitelisted_enquiry_nos_for_latest_estimate_ui:
          response?.whitelisted_enquiry_nos_for_latest_estimate_ui || "",
        confirmation_needed_supplier_id:
          response?.confirmation_needed_supplier_id || "",
        additional_time_required_supplier_id:
          response?.additional_time_required_supplier_id || "",
        orca_allow_md_order_nos:
          response?.orca_allow_md_order_nos ||
          response?.allow_delivery_edit_orders ||
          "",
      };
    },
    { staleTime: Infinity },
  );
};
